import { useMemo } from 'react';

import Lesson from '@/models/Lesson';
import Unit from '@/models/Unit';
import { useQueries } from '@tanstack/react-query';
import { lessonBlocksQueryKeys } from '@/data/services/querykeys';
import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';

type UseUnitsWithLessonsProps = {
  slug: string;
  units: Unit[];
};

const EMPTY_LESSONS: Lesson[] = [];

const useUnitsWithLessons = ({ slug, units }: UseUnitsWithLessonsProps) => {
  const queries = useQueries({
    queries: units.map(unit => ({
      ...lessonBlocksQueryKeys.listLessons(slug, unit.id, {
        pageSize: ORDENABLE_PAGE_SIZE,
      }),
      staleTime: REQUEST_STALE_TIME_IN_MS,
      keepPreviousData: true,
    })),
  });

  const isLoadingLessons = useMemo(
    () =>
      units.reduce((acc, unit, index) => {
        acc[String(unit.id)] = queries[index].isInitialLoading;
        return acc;
      }, {} as Record<string, boolean>),
    [units, queries],
  );

  const queryData = queries.map(q => q.data);

  const childrenMap = useMemo(() => {
    const map: Record<string, Lesson[]> = {};
    units.forEach((unit, index) => {
      const data = queryData[index];
      map[String(unit.id)] = data?.results ?? EMPTY_LESSONS;
    });
    return map;
  }, [units, queryData]);

  return { childrenMap, isLoadingLessons };
};

export default useUnitsWithLessons;
